import React, { useEffect, useRef, useState } from "react";
import { Loader2, Layers, Download, RotateCcw, Archive } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export const DedupePanel = ({ onApplied }) => {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/trivia/dedupe/latest");
      setRun(r.data?.run_id ? r.data : null);
      if (r.data?.status === "scanning") { clearTimeout(timer.current); timer.current = setTimeout(load, 4000); }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, []);

  const scan = async () => { await api.post("/trivia/dedupe/scan"); toast.success("Scan started, this takes a few minutes"); setTimeout(load, 1500); };
  const apply = async () => {
    if (!window.confirm(`Archive ${run.duplicates.toLocaleString()} duplicate questions? You can restore them later.`)) return;
    setBusy(true);
    try { const r = await api.post("/trivia/dedupe/apply", { run_id: run.run_id }); toast.success(`Archived ${r.data.archived}. Vault now ${r.data.total_now.toLocaleString()}`); load(); onApplied(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); } finally { setBusy(false); }
  };
  const restore = async () => {
    if (!window.confirm("Restore every question archived by this run?")) return;
    setBusy(true);
    try { const r = await api.post("/trivia/dedupe/restore", { run_id: run.run_id }); toast.success(`Restored ${r.data.restored}`); load(); onApplied(); }
    finally { setBusy(false); }
  };
  const report = async () => {
    const res = await api.get("/trivia/dedupe/report.csv", { params: { run_id: run.run_id }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `trivia_dedupe_${run.run_id}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const scanning = run?.status === "scanning";
  const pct = run?.total_batches ? Math.round((100 * (run.done_batches || 0)) / run.total_batches) : 0;
  const B = "inline-flex items-center gap-1.5 font-ui text-xs font-semibold px-3 py-2 rounded-sm disabled:opacity-50";

  return (
    <div className="bg-white border border-[#D8CEB9] rounded-sm p-4 mb-6" data-testid="dedupe-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-serif-display text-lg font-bold text-[#1A1615]">Duplicate Cleanup</p>
          <p className="font-ui text-xs text-[#7A6C5D] max-w-xl">Groups questions by answer, Gemini clusters the ones testing the same fact, keeps the best (Editorial, approved, sourced) and archives the rest. Dry run first; nothing changes until you Apply.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="dedupe-scan-btn" onClick={scan} disabled={scanning || busy} className={`${B} bg-[#9E4535] text-white hover:bg-[#82382B]`}>
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />} {scanning ? `Scanning… ${pct}%` : "Run dry-run scan"}
          </button>
          {run?.status === "ready" && <button data-testid="dedupe-apply-btn" onClick={apply} disabled={busy} className={`${B} bg-[#2F6B33] text-white`}><Archive className="w-3.5 h-3.5" /> Apply: archive {run.duplicates.toLocaleString()}</button>}
          {run && ["ready", "applied"].includes(run.status) && <button data-testid="dedupe-report-btn" onClick={report} className={`${B} border border-[#D8CEB9] text-[#4A4036]`}><Download className="w-3.5 h-3.5" /> Full report CSV</button>}
          {run?.status === "applied" && <button data-testid="dedupe-restore-btn" onClick={restore} disabled={busy} className={`${B} border border-[#E2B7B2] text-[#8A1C1C]`}><RotateCcw className="w-3.5 h-3.5" /> Restore</button>}
        </div>
      </div>

      {run && run.status !== "scanning" && run.before != null && (
        <div className="mt-3" data-testid="dedupe-report">
          <p className="font-ui text-sm text-[#1A1615]" data-testid="dedupe-summary">
            <b>{run.before.toLocaleString()}</b> → <b>{(run.after_actual ?? run.after)?.toLocaleString()}</b> questions · <b>{run.duplicates?.toLocaleString()}</b> duplicates in {run.cluster_count?.toLocaleString()} clusters
            <span className="ml-2 font-mono-cv text-[10px] uppercase px-1.5 py-0.5 border border-[#D8CEB9] rounded-sm">{run.status}</span>
            {run.failed_batches > 0 && <span className="ml-2 text-xs text-[#8A5A00]">{run.failed_batches} batch(es) skipped, rerun to catch them</span>}
          </p>
          {run.sample?.length > 0 && (
            <div className="mt-2 max-h-80 overflow-y-auto border border-[#E9E1D0] rounded-sm">
              {run.sample.map((c, i) => (
                <div key={i} className="px-3 py-2 border-b border-[#F0EADF] last:border-0">
                  <p className="font-ui text-xs text-[#1A1615]"><span className="font-semibold">{c.answer}</span> <span className="text-[#7A6C5D]">· {c.category} · keep 1, archive {c.archive.length}</span></p>
                  <p className="font-ui text-xs text-[#2F6B33] mt-0.5">Keep: {c.keep.question}</p>
                  {c.archive.slice(0, 3).map((a) => <p key={a.tv_id} className="font-ui text-xs text-[#9A8C7D] line-through">{a.question}</p>)}
                  {c.archive.length > 3 && <p className="font-ui text-[11px] text-[#9A8C7D]">+{c.archive.length - 3} more</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
